import { signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { setEntities, setEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { ICompetition } from '@models/competition';
import {
  setCreated,
  setCreating,
  setLoaded,
  setLoading,
  setLoadingError,
  withCallStatus,
} from '@ngrx-helpers/call-status';
import { CompetitionApiService } from '../api/competition-api.service';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, lastValueFrom, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { randomId } from '@string-helpers/random-id';
import { waitForFalse } from '@ngrx-helpers/wait';
import { createOperationAsync, loadOperationAsync } from '@ngrx-helpers/call-status-operations';

function updateEmailStatus(c: ICompetition): ICompetition {
  let mailing_status: ICompetition['mailing_status'] = 'not_sent';
  switch (c.mailing_status) {
    case 'not_sent':
      mailing_status = 'first_email';
      break;
    case 'first_email':
      mailing_status = 'second_email';
      break;
    default:
      mailing_status = 'third_email';
      break;
  }

  return {
    ...c,
    mailing_status,
  };
}

export const CompetitionsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('competitions'),

  withEntities<ICompetition>(),
  withCallStatus<ICompetition['id']>(),
  withState<{
    isSendingEmails: boolean;
    sendingEmailsError: Error | null;
  }>({
    isSendingEmails: false,
    sendingEmailsError: null,
  }),

  withProps(() => ({
    api: inject(CompetitionApiService),
  })),

  withMethods(state => ({
    loadCompetitions: rxMethod<void>(
      pipe(
        tap(() => updateState(state, 'load competitions', setLoading())),
        exhaustMap(() =>
          state.api.getCompetitions().pipe(
            tapResponse(
              competitions =>
                updateState(state, 'competitions loaded', setLoaded(), setEntities(competitions)),
              (error: Error) =>
                updateState(state, 'competitions load error', setLoadingError(error))
            )
          )
        )
      )
    ),

    createCompetition: rxMethod<{ start_date: string; end_date: string; tempId: string }>(
      pipe(
        tap(({ tempId }) => updateState(state, 'create competition', setCreating(tempId))),
        switchMap(({ start_date, end_date, tempId }) =>
          state.api.createCompetition(start_date, end_date).pipe(
            tapResponse(
              competition =>
                updateState(
                  state,
                  'competition created',
                  setCreated(tempId, competition.id),
                  setEntity(competition)
                ),
              (error: Error) =>
                updateState(state, 'competition create error', setLoadingError(error, tempId))
            )
          )
        )
      )
    ),

    sendEmails: rxMethod<string>(
      pipe(
        tap(() =>
          updateState(state, 'send emails start', () => ({
            isSendingEmails: true,
            sendingEmailsError: null,
          }))
        ),
        switchMap(id =>
          state.api.sendEmails(id).pipe(
            tapResponse(
              () =>
                updateState(
                  state,
                  'emails sent',
                  () => ({ isSendingEmails: false }),
                  updateEntity({ id, changes: updateEmailStatus })
                ),
              (sendingEmailsError: Error) =>
                updateState(state, 'emails send error', () => ({
                  isSendingEmails: false,
                  sendingEmailsError,
                }))
            )
          )
        )
      )
    ),
  })),

  withMethods(state => ({
    async waitForLoadingCompetitions(force = false) {
      return await loadOperationAsync({
        state,
        load: s => s.loadCompetitions(),
        result: s => s.entities(),
        force,
      });
    },

    async waitForCreatingCompetition(start_date: string, end_date: string, tempId = randomId()) {
      return await createOperationAsync({
        state,
        tempId,
        create: s => s.createCompetition({ start_date, end_date, tempId }),
        result: (s, id) => s.entityMap()[id],
      });
    },

    async waitForSendingEmails(id: string) {
      state.sendEmails(id);
      await lastValueFrom(waitForFalse(state.isSendingEmails));

      const error = state.sendingEmailsError();
      if (error) throw error;

      return;
    },
  }))
);
